import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { isThisMonth } from 'date-fns/isThisMonth';
import { startOfToday } from 'date-fns/startOfToday';
import { dateFormats } from 'month-view-page-common';

import { formatDateNonlocalised } from '../../../../bootstrap/localisation';
import {
  getSelectedInboundDate,
  getSelectedMonth,
  getSelectedOutboundDate,
  getSelectedOutboundMonth,
  isLoading,
  isReturn,
} from '../../selectors';
import {
  MP_PROPERTY_TOTAL_COVERAGE,
  MP_PROPERTY_TOTAL_DAYS,
  MP_PROPERTY_SEEN_COVERAGE,
  MP_PROPERTY_SEEN_DAYS,
  MINI_EVENT,
  DEPART,
  ME_PROPERTY_FULFILLED_RATIO_DEPART,
  ME_PROPERTY_FULFILLED_RATIO_RETURN,
} from '../constants/analyticsConstants';
import * as dataTypeConstants from '../constants/dataTypeConstants';
import * as mapping from '../data-parsing/analyticsMappingParser';
import {
  getSelectableDaysCount,
  getGridPricedDaysCount,
} from '../data-parsing/gridEntryHelper';

import type { Direction, State } from '../../selectors/types';

const addTotalCoverageData = (
  dataObject: Record<string, string | number>,
  state: State,
) => {
  const props: Record<string, number> = {};
  const returnTrip = isReturn(state);

  const selectableOutboundDays = getSelectableDaysCount(
    state,
    dataTypeConstants.OUTBOUND_DIRECTION,
  );

  const outboundMonthDate = getSelectedOutboundMonth(state);

  let allDays = getDaysInMonth(outboundMonthDate);
  let allPricedDays = getGridPricedDaysCount(
    state,
    dataTypeConstants.OUTBOUND_DIRECTION,
  );

  let allSeenDays = selectableOutboundDays.validDayCount;
  let allPricedSeenDays = selectableOutboundDays.pricedDayCount;
  if (returnTrip) {
    const inboundMonthDate = getSelectedMonth(
      state,
      dataTypeConstants.INBOUND_DIRECTION,
    );
    allDays += inboundMonthDate ? getDaysInMonth(inboundMonthDate) : NaN;
    allPricedDays += getGridPricedDaysCount(
      state,
      dataTypeConstants.INBOUND_DIRECTION,
    );
    const selectableInboundDays = getSelectableDaysCount(
      state,
      dataTypeConstants.INBOUND_DIRECTION,
    );
    const returnSeenDays = selectableInboundDays.validDayCount;
    const returnPricedSeenDays = selectableInboundDays.pricedDayCount;
    allSeenDays += returnSeenDays;
    allPricedSeenDays += returnPricedSeenDays;
  }

  props[MP_PROPERTY_TOTAL_COVERAGE] = Math.ceil(
    (allPricedDays / allDays) * 100,
  );
  props[MP_PROPERTY_TOTAL_DAYS] = allDays;
  props[MP_PROPERTY_SEEN_COVERAGE] = Math.ceil(
    (allPricedSeenDays / allSeenDays) * 100,
  );
  props[MP_PROPERTY_SEEN_DAYS] = allSeenDays;

  return { ...dataObject, ...props };
};

const addTrackDataForDirection = (
  dataObject: Record<string, string | number>,
  direction: Direction,
  state: State,
  logType: string,
) => {
  const props: Record<string, number> = {};
  const monthDate = getSelectedMonth(state, direction);
  const daysInMonth = monthDate ? getDaysInMonth(monthDate) : NaN;
  const isCurrentMonth = monthDate && isThisMonth(monthDate);
  let availableDays = daysInMonth;
  if (isCurrentMonth)
    availableDays = availableDays + 1 - startOfToday().getDate();
  const daysWithQuotes = getGridPricedDaysCount(state, direction);
  const possibleDaysRatio = Math.ceil((availableDays / daysInMonth) * 100);
  const fulfilledDaysRatio = Math.ceil((daysWithQuotes / availableDays) * 100);

  const directionLabel = mapping.directionHelper(direction);
  const labels = {
    possibleLabel: `Possible ${directionLabel} Days in Month Ratio Percent`,
    fulfilledLabel: `Priced ${directionLabel} Result Ratio Percent`,
    totalDaysLabel: `Total Days in ${directionLabel} Month`,
    availableDaysLabel: `Available Days in ${directionLabel} Month`,
    daysWithQuotesLabel: `${directionLabel} Days with Quotes`,
  };

  if (logType === MINI_EVENT) {
    labels.fulfilledLabel =
      directionLabel === DEPART
        ? ME_PROPERTY_FULFILLED_RATIO_DEPART
        : ME_PROPERTY_FULFILLED_RATIO_RETURN;

    props[labels.fulfilledLabel] = fulfilledDaysRatio;
    return { ...dataObject, ...props };
  }

  props[labels.possibleLabel] = possibleDaysRatio;
  props[labels.fulfilledLabel] = fulfilledDaysRatio;
  props[labels.totalDaysLabel] = daysInMonth;
  props[labels.availableDaysLabel] = availableDays;
  props[labels.daysWithQuotesLabel] = daysWithQuotes;

  return { ...dataObject, ...props };
};

const coverageTracker = (
  dataObject: Record<string, string | number>,
  state: State,
  initialOutboundMonth: Date | null,
  initialInboundMonth: Date | null,
  logType: string,
) => {
  let props: Record<string, string | number> = {};
  const returnTrip = isReturn(state);
  const inboundDate = getSelectedInboundDate(state);

  if (initialOutboundMonth)
    props['Initial Outbound Month'] = `${formatDateNonlocalised(
      initialOutboundMonth,
      dateFormats.YEAR_MONTH_DASHED,
    )}`;
  if (returnTrip && initialInboundMonth)
    props['Initial Inbound Month'] = `${formatDateNonlocalised(
      initialInboundMonth,
      dateFormats.YEAR_MONTH_DASHED,
    )}`;

  props['Outbound Search Date'] = `${formatDateNonlocalised(
    getSelectedOutboundDate(state),
    dateFormats.YEAR_MONTH_DASHED,
  )}`;
  if (returnTrip && inboundDate)
    props['Inbound Search Date'] = `${formatDateNonlocalised(
      inboundDate,
      dateFormats.YEAR_MONTH_DASHED,
    )}`;

  const loading = isLoading(state);
  if (!loading) {
    props = addTrackDataForDirection(
      props,
      dataTypeConstants.OUTBOUND_DIRECTION,
      state,
      logType,
    );
    if (returnTrip) {
      props = addTrackDataForDirection(
        props,
        dataTypeConstants.INBOUND_DIRECTION,
        state,
        logType,
      );
    }
    props = addTotalCoverageData(props, state);
  }

  return { ...dataObject, ...props };
};

export default coverageTracker;
